import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { useLocation, useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const EMPTY_PAGE = {
  client: '',
  vertical: '',
  name: '',
  url: '',
};

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return String(data.non_field_errors[0]);
  }
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).find((value) => Array.isArray(value) ? value.length > 0 : Boolean(value));
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }
  return fallback;
}

export default function ClientLandingPagesPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientId = String(
    location?.state?.clientId ?? searchParams.get('client_id') ?? ''
  );

  const [rows, setRows] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [filterClient, setFilterClient] = useState(initialClientId);
  const [filterVertical, setFilterVertical] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(EMPTY_PAGE);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const loadVerticals = async () => {
    try {
      const res = await api.request({ url: 'dashboard/verticals/', method: 'GET' });
      setVerticals(Array.isArray(res.data) ? res.data : []);
    } catch (_err) {
      // Keep page usable if verticals fail.
    }
  };

  const loadLandingPages = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        client_id: filterClient || undefined,
        vertical_id: filterVertical || undefined,
        search: search || undefined,
      };
      const res = await api.request({
        url: 'dashboard/client-landing-pages/',
        method: 'GET',
        payload,
      });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      data.forEach((row) => {
        if (row.client && row.client_name) {
          addClientOption({ value: row.client, label: row.client_name });
        }
      });
      setRows(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load landing pages'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVerticals();
    loadLandingPages();
    // One-time load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentClientId = searchParams.get('client_id') || '';
    const nextClientId = filterClient ? String(filterClient) : '';
    if (currentClientId === nextClientId) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    if (nextClientId) {
      nextParams.set('client_id', nextClientId);
    } else {
      nextParams.delete('client_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [filterClient, searchParams, setSearchParams]);

  const handleOpen = (row = EMPTY_PAGE) => {
    setCurrent({
      ...EMPTY_PAGE,
      ...row,
      client: row.client || '',
      vertical: row.vertical || '',
      name: row.name || '',
      url: row.url || '',
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCurrent(EMPTY_PAGE);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      client: current.client,
      vertical: current.vertical,
      name: current.name,
      url: current.url,
    };
    try {
      if (current.id) {
        await api.request({
          url: `dashboard/client-landing-pages/${current.id}/`,
          method: 'PUT',
          payload,
        });
      } else {
        await api.request({
          url: 'dashboard/client-landing-pages/',
          method: 'POST',
          payload,
        });
      }
      handleClose();
      await loadLandingPages();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save landing page'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this landing page?')) return;
    setError('');
    try {
      await api.request({
        url: `dashboard/client-landing-pages/${id}/`,
        method: 'DELETE',
      });
      await loadLandingPages();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete landing page'));
    }
  };

  // Same pull as the nightly job, run now, so a page built today in
  // LeadsHook Pro doesn't wait until tomorrow to show up here.
  const handleSync = async () => {
    setSyncing(true);
    setError('');
    setSyncReport(null);
    try {
      const res = await api.request({
        url: 'dashboard/client-landing-pages/sync-leadshookpro/',
        method: 'POST',
      });
      setSyncReport(res.data);
      await loadLandingPages();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not sync from LeadsHook Pro'));
    } finally {
      setSyncing(false);
    }
  };

  const visibleRows = useMemo(() => rows, [rows]);

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Client Landing Pages
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadLandingPages} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync from LeadsHook Pro
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Landing Page
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={clientOptions}
                loading={clientOptionsLoading}
                value={findClientOptionByValue(filterClient) || null}
                onInputChange={(_event, value) => handleClientSearch(value)}
                onChange={(_event, value) => setFilterClient(value?.value || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="All clients" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Vertical"
                value={filterVertical}
                onChange={(event) => setFilterVertical(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((vertical) => (
                  <MenuItem key={vertical.id} value={vertical.id}>
                    {vertical.vertical}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Name or URL"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadLandingPages} disabled={loading} sx={{ height: '100%' }}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {syncReport ? (
        <Alert
          severity={syncReport.unresolved_client?.length || syncReport.unresolved_vertical?.length ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setSyncReport(null)}
        >
          <Typography variant="body2">
            Synced from LeadsHook Pro: {syncReport.created} new, {syncReport.updated} updated,{' '}
            {syncReport.deactivated} turned off.
          </Typography>
          {syncReport.unresolved_client?.length ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Not matched to a client (set the company's external client id in LeadsHook Pro):{' '}
              {syncReport.unresolved_client
                .map((row) => `${row.company || 'Unknown'} (${row.url})`)
                .join(', ')}
            </Typography>
          ) : null}
          {syncReport.unresolved_vertical?.length ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Not matched to a vertical:{' '}
              {syncReport.unresolved_vertical
                .map((row) => `${row.company || 'Unknown'} — ${row.vertical_label || row.vertical_slug || '?'}`)
                .join(', ')}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Vertical</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No landing pages found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.client_name || row.client}</TableCell>
                        <TableCell>{row.vertical_name || row.vertical}</TableCell>
                        <TableCell>{row.name || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 480 }}>
                          <Typography
                            component="a"
                            href={row.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              display: 'inline-block',
                              maxWidth: '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            title={row.url}
                          >
                            {row.url}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpen(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{current.id ? 'Edit Landing Page' : 'Add Landing Page'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Autocomplete
              options={clientOptions}
              loading={clientOptionsLoading}
              value={findClientOptionByValue(current.client) || null}
              onInputChange={(_event, value) => handleClientSearch(value)}
              onChange={(_event, value) => setCurrent((prev) => ({ ...prev, client: value?.value || '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Client" required />
              )}
            />
            <TextField
              select
              label="Vertical"
              value={current.vertical}
              onChange={(event) => setCurrent((prev) => ({ ...prev, vertical: event.target.value }))}
              required
            >
              {verticals.map((vertical) => (
                <MenuItem key={vertical.id} value={vertical.id}>
                  {vertical.vertical}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={current.name}
              onChange={(event) => setCurrent((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Optional display name"
            />
            <TextField
              label="URL"
              type="url"
              value={current.url}
              onChange={(event) => setCurrent((prev) => ({ ...prev, url: event.target.value }))}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !current.client || !current.vertical || !current.url}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
